// ==================== FILE: src/services/brandingService.js ====================
// Enhanced Branding API Service - Fetches, caches, and applies branding configuration

import { settingsService } from './settingsService';
import { setTenantId } from './tenantConfig';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;
const CACHE_KEY = 'resto_branding_data_v4';
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour

class BrandingService {
  constructor() {
    this.cache = null;
    this.appliedBranding = null;
    this.initData = null;
  }

  /**
   * Fetch initial configuration (Tenant ID, Branding, Settings)
   */
  async fetchInitData() {
    try {
      console.log('🌍 Fetching real init data from API...');
      const initUrl = `${API_BASE_URL}/ordering/init`;
      const response = await fetch(initUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Api-Key': X_API_KEY
        }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      // Use API content directly
      const apiContent = result.content || {};
      result.content = apiContent;

      console.log('📋 API legal_pages:', result.legal_pages ? `${result.legal_pages.length} pages found` : 'NOT present in API response');
      console.log('📋 API response keys:', Object.keys(result));

      return this.processInitResponse(result);

    } catch (error) {
      console.error('❌ Error fetching init data:', error);
      return null;
    }
  }

  /**
   * Standardized processing of the Init response (shared between Sample and Real)
   */
  processInitResponse(result) {
    if (!result?.status || !result?.restaurant) {
      throw new Error('Invalid Init API response structure');
    }

    console.log('✅ Init data processed:', result.restaurant.name);

    // 1. Store Tenant ID centrally
    const tenantId = result.restaurant.id;
    if (tenantId) setTenantId(tenantId);

    // 3. Robustly extract legal pages from the API response
    let extractedPolicies = [];
    if (Array.isArray(result.legal_pages) && result.legal_pages.length > 0) {
      extractedPolicies = result.legal_pages;
    } else if (result.restaurant && Array.isArray(result.restaurant.legal_pages) && result.restaurant.legal_pages.length > 0) {
      extractedPolicies = result.restaurant.legal_pages;
    } else if (result.pages && Array.isArray(result.pages)) {
      extractedPolicies = result.pages;
    } else if (result.settings) {
      // Fallback for some Laravel backends that put policies directly in settings
      const possiblePolicies = [
        { type: 'privacy_policy', title: 'Privacy Policy' },
        { type: 'terms_and_conditions', title: 'Terms & Conditions' },
        { type: 'refund_policy', title: 'Refund Policy' },
        { type: 'cancellation_policy', title: 'Cancellation Policy' },
        { type: 'about_us', title: 'About Us' }
      ];
      possiblePolicies.forEach(p => {
        if (result.settings[p.type] || result.settings[p.type.replace(/_/g, '-')]) {
          extractedPolicies.push({
            type: p.type,
            title: p.title,
            content: result.settings[p.type] || result.settings[p.type.replace(/_/g, '-')]
          });
        }
      });
    }



    const branding = result.restaurant.branding || {};
    const mappedBrandingData = {
      theme_colors: {
        primary: branding.primary_color,
        secondary: branding.secondary_color,
        accent: branding.primary_color,
        text: branding.text_color,
        background: '#FFFFFF'
      },
      brand_assets: {
        primary_logo: branding.primary_logo,
        secondary_logo: branding.secondary_logo,
        banner_image: branding.banner_image,
        favicon: branding.favicon
      },
      typography: {
        font_family: branding.font_family,
        font_weight: branding.font_weight || 'regular'
      },
      ui_elements: {
        button_shape: 'rounded',
        card_shape: 'rounded',
        image_corners: 'rounded'
      },
      settings: result.settings || {},
      restaurant: result.restaurant || {},
      template: result.restaurant.template || null,
      content: result.content || {},
      legal_pages: extractedPolicies
    };

    this.initData = mappedBrandingData;

    // Persist settings
    if (result.settings) {
      settingsService.save(result.settings);
    }

    // Update cache
    this.cache = mappedBrandingData;
    localStorage.setItem(CACHE_KEY, JSON.stringify(mappedBrandingData));

    return mappedBrandingData;
  }

  /**
   * Get cached branding data if available and fresh
   */
  getCachedBranding() {
    try {
      // Check memory cache first
      if (this.cache) {
        const age = Date.now() - (this.cache.timestamp || 0);
        if (age < CACHE_DURATION) {
          console.log('✅ Using memory cached branding');
          return this.cache;
        }
      }

      // Check localStorage cache
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const data = JSON.parse(cached);
      const age = Date.now() - (data.timestamp || 0);

      // Return cached data if fresh
      if (age < CACHE_DURATION) {
        console.log('✅ Using fresh cached branding from localStorage');
        this.cache = data;
        return data;
      }

      console.log('⚠️ Cached branding expired');
      return null;

    } catch (error) {
      console.error('❌ Error reading cached branding:', error);
      return null;
    }
  }

  /**
   * Get branding with automatic caching
   */
  async getBranding() {
    // Try cache first
    const cached = this.getCachedBranding();
    if (cached) {
      return cached;
    }

    // Fetch fresh data from Init API
    return await this.fetchInitData();
  }

  /**
   * Clean color value (remove backticks and extra formatting)
   */
  cleanColorValue(color) {
    if (!color) return null;
    return color.replace(/[`'"]/g, '').trim();
  }

  /**
   * Apply branding colors to CSS variables
   */
  applyBrandingColors(brandingData) {
    if (!brandingData?.theme_colors) {
      console.warn('⚠️ No theme colors found in branding data');
      return;
    }

    const colors = brandingData.theme_colors;

    const primary = this.cleanColorValue(colors.primary);
    const secondary = this.cleanColorValue(colors.secondary);
    const accent = this.cleanColorValue(colors.accent);
    const text = this.cleanColorValue(colors.text);
    const background = this.cleanColorValue(colors.background);

    console.log('🎨 Applying branding colors:', { primary, secondary, accent, text, background });

    // We no longer manually touch the DOM here. 
    // The ThemeProvider in App.js uses generateCSSVariables from TemplateStyles.js
    // which consumes this branding data to apply all variables to a wrapper div.
    // This prevents specificity conflicts and "jumping" styles.
    this.appliedBranding = { colors: { primary, secondary, accent, text, background } };
  }

  /**
   * Apply branding images
   */
  applyBrandingImages(brandingData) {
    if (!brandingData?.brand_assets) {
      console.warn('⚠️ No brand assets found in branding data');
      return;
    }

    const assets = brandingData.brand_assets;
    const root = document.documentElement;

    if (assets.primary_logo) {
      sessionStorage.setItem('branding_primary_logo', assets.primary_logo);
    }

    if (assets.secondary_logo) {
      sessionStorage.setItem('branding_secondary_logo', assets.secondary_logo);
    }

    if (assets.banner_image) {
      root.style.setProperty('--bg-banner', `url(${assets.banner_image})`);
      sessionStorage.setItem('branding_banner_image', assets.banner_image);
    }

    if (assets.favicon) {
      this.updateFavicon(assets.favicon);
    }

    console.log('✅ Branding images applied');
  }

  /**
   * Update favicon dynamically
   */
  updateFavicon(faviconUrl) {
    try {
      const existingLinks = document.querySelectorAll("link[rel*='icon']");
      existingLinks.forEach(link => link.remove());

      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/x-icon';
      link.href = faviconUrl;
      document.head.appendChild(link);

      console.log('✅ Favicon updated:', faviconUrl);
    } catch (error) {
      console.error('❌ Error updating favicon:', error);
    }
  }

  /**
   * Apply typography settings
   */
  applyTypography(brandingData) {
    if (!brandingData?.typography) {
      console.warn('⚠️ No typography settings found in branding data');
      return;
    }

    const typography = brandingData.typography;

    if (typography.font_family) {
      this.loadGoogleFont(typography.font_family);
      // Variables applied via ThemeProvider
    }
    console.log('✅ Typography settings recorded');
  }

  /**
   * Load Google Font dynamically
   */
  loadGoogleFont(fontFamily) {
    try {
      const linkId = 'dynamic-google-font';
      let link = document.getElementById(linkId);

      if (!link) {
        link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }

      const fontName = fontFamily.replace(/\s+/g, '+');
      link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@300;400;500;600;700&display=swap`;

      console.log('✅ Google Font loaded:', fontFamily);
    } catch (error) {
      console.error('❌ Error loading Google Font:', error);
    }
  }

  /**
   * Apply UI elements settings
   */
  applyUIElements(brandingData) {
    if (!brandingData?.ui_elements) {
      console.warn('⚠️ No UI elements settings found in branding data');
      return;
    }



    // Variables applied via ThemeProvider
    console.log('✅ UI elements settings recorded');
  }

  /**
   * Apply complete branding (colors, images, typography, UI)
   */
  async applyCompleteBranding() {
    try {
      console.log('🎨 Applying complete branding configuration...');

      const brandingData = await this.getBranding();

      if (!brandingData) {
        console.warn('⚠️ No branding data available');
        return null;
      }

      this.applyBrandingColors(brandingData);
      this.applyBrandingImages(brandingData);
      this.applyTypography(brandingData);
      this.applyUIElements(brandingData);

      console.log('✅ Complete branding applied successfully');

      return brandingData;
    } catch (error) {
      console.error('❌ Error applying branding:', error);
      return null;
    }
  }
}

// Export singleton instance
export const brandingService = new BrandingService();

// Export class for testing
export default BrandingService;